import { useState, useMemo, useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import Arrow from 'src/assets/images/arrow-down.svg';
import ArrowEnabled from 'src/assets/images/arrow-dark.svg';
import { IOption } from 'src/config/types';
import { Portal } from './portal';
import { usePopper } from 'react-popper';
import { getIn } from 'formik';

type SelectProps = {
  value: string | number;
  options: IOption[];
  optionClasses?: string;
  onChange?: (value: string | number, option?: any) => void;
  placeholder?: string;
  placeholderColor?: string;
  required?: boolean;
  searchable?: boolean;
  className?: string;
  disabled?: boolean; // only implemented for non-searchable
  error?: string;
  label?: string;
  view?: boolean;
  onBlur?: (e: React.FocusEvent<any, Element>) => void;
  name?: string;
  isReadOnly?: boolean;
  mainContainerClass?: string;
  rightText?: string;
  btnLabeledClassName?: string;
  handleError?: any;
  handleTouched?: any;
};

export default function Select({
  label,
  placeholder,
  placeholderColor = 'text-slate-500',
  options,
  value,
  onChange,
  required = false,
  searchable = false,
  className,
  disabled = false,
  error,
  view,
  onBlur,
  name,
  optionClasses,
  isReadOnly,
  mainContainerClass,
  rightText,
  btnLabeledClassName,
  handleError,
  handleTouched,
}: SelectProps) {
  const [visible, setVisible] = useState(false);
  const [wrapperRef, setWrapperRef] = useState<HTMLDivElement | null>(null);
  const [childElementRef, setChildElementRef] = useState<HTMLDivElement | null>(
    null
  );

  useEffect(() => {
    const handleClick = (e: any) => {
      if (
        wrapperRef &&
        !wrapperRef.contains(e.target) &&
        childElementRef &&
        !childElementRef?.contains(e.target)
      ) {
        setVisible(false);
      }
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, [wrapperRef, childElementRef]);

  const [searchText, setSearchText] = useState(
    () => options?.find((o) => o.value === value)?.label || ''
  );

  const selected = useMemo(() => {
    if (value || value === '') {
      return options?.find(
        (o) =>
          o.value === value ||
          o.value.toString().toLowerCase() === value.toString().toLowerCase()
      );
    }
  }, [options, value]);

  const FormikError = (errors: any, touched: any, fieldName: any) => {
    const error = getIn(errors, fieldName);
    const touch = getIn(touched, fieldName);
    return touch && error ? error : null;
  };

  const errorMessage = FormikError(handleError, handleTouched, name);

  const optionList = useMemo(() => {
    const q = searchText.toLowerCase();
    return searchable && q && searchText !== value && visible
      ? options.filter((o) => o.label.toLowerCase().match(q))
      : options;
  }, [options, value, searchable, searchText]);

  const visibleStyle = visible && 'rotate-180 opacity-80';
  const [fs, color, mt] =
    (searchable && searchText) || value
      ? ['14', '[text-slate-500]', 'top-1.5']
      : ['14', '[text-slate-500]', 'top-1.5'];
  const disabledStyle = disabled
    ? 'cursor-not-allowed border-zinc-200 hover:border-zinc-200 bg-slate-100 font-normal text-opacity-50 text-blue-950 placeholder:text-opacity-50'
    : '';
  let mergedClasses = twMerge(
    `relative inline-block p-4 bg-white rounded-full border border-zinc-200 text-left focus:ring-lightBlue focus:border-transparent hover:border-borderBlue cursor-pointer ${
      error ? 'border border-rose-500' : ''
    } ${disabledStyle} ${className} ${errorMessage && 'border-rose-500'}`
  );

  const optionsWidth = wrapperRef?.clientWidth;
  const { styles, attributes } = usePopper(wrapperRef, childElementRef, {
    placement: 'bottom',
    modifiers: [
      {
        name: 'offset',
        enabled: true,
        options: {
          offset: [0, 0],
        },
      },
    ],
  });

  const errorClassNames =
    'block -ml-4 mt-6 font-SFProDisplay text-xs text-rose-500';

  return (
    <div className={mainContainerClass}>
      <Label
        disabled={disabled}
        label={label}
        view={view}
        required={required}
      />
      <div
        className={mergedClasses}
        ref={setWrapperRef}
        onClick={(e) => {
          e.stopPropagation();
          if (!disabled) {
            setVisible(!visible);
          }
        }}
      >
        {searchable ? (
          <div
            className="relative flex items-center h-full border-none"
            onClick={() => !isReadOnly && setVisible(true)}
          >
            <input
              id="select-search"
              type="text"
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
              onBlur={onBlur}
              readOnly={isReadOnly}
              autoComplete="off"
              className="font-medium bg-transparent w-[85%] pl-0 text-[#2D2D3B] text-[16px] rounded-[8px] h-full border-none focus:border-transparent border-transparent focus:ring-0"
            />
            {!searchText && (
              <label
                htmlFor="select-search"
                className={`absolute left-0 font-medium pointer-events-none text-[${fs}px] ${mt} text-${color} font-medium ${placeholderColor}`}
              >
                {placeholder}
              </label>
            )}
            <img
              src={disabled ? Arrow : ArrowEnabled}
              alt="down arrow"
              className={`absolute right-2 cursor-pointer transform transition-transform duration-300 ease-in-out ${visibleStyle}`}
            />
          </div>
        ) : (
          <div className="relative h-full flex items-center">
            <button
              type="button"
              onBlur={onBlur}
              disabled={disabled}
              name={name}
              onClick={() => {
                if (!disabled && !isReadOnly) {
                  setVisible(!visible);
                }
              }}
              className={`${
                selected?.label === 'Select' && 'opacity-50'
              } inline-flex peer font-SFProDisplay font-normal tracking-[0.008em] w-full leading-[20px] text-blue-950  h-full items-center text-sm focus:outline-none  justify-${
                value || value === '' ? 'between' : 'end'
              }
              ${disabled ? 'cursor-not-allowed' : ''}
              ${view ? 'pointer-events-none' : ''}
              ${errorMessage && 'border-rose-500'}
              `}
              id={name}
              aria-expanded="true"
              aria-haspopup="true"
            >
              <p
                className={twMerge(
                  `font-normal capitalize ${
                    disabled ? 'text-opacity-50 text-blue-950' : 'text-blue-950'
                  } inline-block whitespace-nowrap overflow-hidden text-ellipsis`,
                  btnLabeledClassName ?? btnLabeledClassName
                )}
              >
                {selected?.label}
              </p>
              {!view ? (
                <div className="flex gap-3 items-center">
                  <span className="text-stone-800 text-opacity-80 text-base font-normal leading-normal">
                    {rightText}
                  </span>
                  <img
                    src={ArrowEnabled}
                    alt="down arrow"
                    className={`h-[8px] transform transition-transform duration-300 ease-in-out ${visibleStyle} `}
                  />
                </div>
              ) : null}
            </button>
            {value || searchText ? null : (
              <label
                htmlFor="menu-button"
                className={`absolute font-SFProDisplay left-0 top-1/2 -translate-y-1/2 pointer-events-none text-[${fs}px] ${
                  disabled ? 'text-[#D1D5DB]' : `text-${color}`
                } !font-medium ${placeholderColor}`}
              >
                {placeholder}
              </label>
            )}
          </div>
        )}
        <Portal>
          {visible && (
            <div
              className="absolute z-100 right-0 max-h-[300px] overflow-x-hidden overflow-y-auto mt-1 origin-top-right rounded-[8px] bg-white shadow-lg "
              role="menu"
              aria-orientation="vertical"
              aria-labelledby="menu-button"
              ref={setChildElementRef}
              style={{ ...styles.popper, width: `${optionsWidth}px` }}
              {...attributes.popper}
            >
              <div className="py-1 select-options-list" role="none">
                {optionList?.length ? (
                  optionList?.map((option, i) => {
                    return (
                      <div
                        key={`option-${option.label}-${i}`}
                        onClick={(e) => {
                          e.stopPropagation();
                          onChange?.(option.value, option);
                          setVisible(false);
                          if (searchable) setSearchText(option.label);
                        }}
                        className={twMerge(
                          'select-option cursor-pointer font-SFProDisplay hover:bg-gray-100 flex justify-between items-center min-h-[48px] px-4 py-1 w-full font-normal text-xs',
                          option.className ?? option.className
                        )}
                      >
                        <span
                          className={twMerge(
                            `text-base capitalize`,
                            optionClasses
                          )}
                        >
                          {option.label}
                        </span>
                        {option.extraContent && option.extraContent}
                        {option.rightText && <span>{option.rightText}</span>}
                      </div>
                    );
                  })
                ) : (
                  <div className="font-SFProDisplay flex items-center justify-center text-zinc-500 h-[48px] px-4 py-1 w-full font-normal text-xs">
                    No Data Found
                  </div>
                )}
              </div>
            </div>
          )}
        </Portal>
        {error ? <p className={errorClassNames}>{error}</p> : null}
        <span className={errorClassNames}>{errorMessage}</span>
      </div>
    </div>
  );
}

interface ILable {
  disabled: boolean;
  view?: boolean;
  label?: string;
  required?: boolean;
}

const Label = ({ disabled, view, label, required = false }: ILable) => {
  if (!label?.length) return null;
  return (
    <label
      className={`mb-2 inline-block font-medium font-SFProDisplay text-blue-950 text-sm ${
        disabled && !view ? ' cursor-not-allowed' : ''
      }`}
    >
      {label}
      {required && <span className="text-[red] ml-1">*</span>}
    </label>
  );
};
